<template>
  <button type="button" id="baseButton" :class="propsClass">
    <template v-if="loading">
      <span
        class="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent rounded-full"
        role="status"
        aria-label="loading">
      </span>
    </template>
    <template v-else>
      <slot></slot>
    </template>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva } from 'class-variance-authority';
type Props = {
  size?: 'base' | 'sm' | 'lg' | 'full' | 'icon' | 'xs';
  pill?: boolean;
  outline?: boolean;
  disabled?: boolean;
  loading?: boolean;
  color?: 'primary' | 'dark' | 'light' | 'green' | 'red' | 'purple' | 'custom' | 'ghost';
};

const props = withDefaults(defineProps<Props>(), {
  size: 'base',
  pill: false,
  outline: false,
  color: 'primary',
  loading: false,
});

const colors = ['primary', 'dark', 'light', 'green', 'red', 'purple', 'ghost'] as const;
type ColorType = (typeof colors)[number];

const propsClass = computed(() => {
  return cva('flex items-center justify-center text-sm rounded-md', {
    variants: {
      size: {
        xs: 'px-2 py-1 text-sm',
        sm: 'px-3 py-2 text-sm',
        base: 'px-5 py-2.5 text-sm',
        lg: 'px-5 py-3 text-base',
        full: 'px-5 py-2.5 w-full',
        icon: 'p-2.5 text-lg',
      },
      pill: {
        true: 'rounded-full',
        false: 'rounded-lg',
      },
      outline: {
        true: '',
        false: 'focus:outline-none',
      },
      disabled: {
        true: 'bg-opacity-80 cursor-not-allowed text-slate-400 bg-slate-100',
        false: 'bg-opacity-1',
      },
      color: Object.fromEntries(colors.map((color) => [color, ''])) as Record<ColorType, string>,
    },
    compoundVariants: [
      // SOLID BUTTONS (outline: false)
      ...colors
        .filter((c) => c !== 'ghost')
        .map((color) => ({
          outline: false,
          disabled: false,
          color: color as ColorType,
          class: {
            primary: 'text-white bg-blue-700 border border-blue-700 hover:bg-blue-800',
            dark: 'text-white bg-gray-800 hover:bg-gray-900',
            light: 'text-gray-900 bg-white border border-gray-300 hover:bg-gray-100',
            green: 'text-white bg-green-700 hover:bg-green-800',
            red: 'text-white bg-red-700 hover:bg-red-800',
            purple: 'text-white bg-purple-700 hover:bg-purple-800',
            ghost: '',
          }[color],
        })),

      // OUTLINED BUTTONS (outline: true)
      ...colors.map((color) => ({
        outline: true,
        disabled: false,
        color: color as ColorType,
        class: {
          primary: 'text-blue-700 hover:text-white border border-blue-700 hover:bg-blue-800',
          dark: 'text-gray-900 hover:text-white border border-gray-800 hover:bg-gray-900',
          light:
            'hover:bg-theme-100 hover:border-theme-300 hover:text-theme-500 border text-theme-500 border-gray-300',
          green: 'text-green-700 hover:text-white border border-green-700 hover:bg-green-800',
          red: 'text-red-700 hover:text-white border border-red-700 hover:bg-red-800',
          purple: 'text-purple-700 hover:text-white border border-purple-700 hover:bg-purple-800',
          ghost:
            'text-theme-600 hover:text-theme-800 bg-theme-50 rounded-md font-light border border-theme-200',
        }[color],
      })),

      // DISABLED SOLID BUTTONS
      ...colors
        .filter((c) => c !== 'ghost')
        .map((color) => ({
          outline: false,
          disabled: true,
          color: color as ColorType,
          class: {
            primary: 'text-white bg-blue-700 border border-blue-700',
            dark: 'text-white bg-gray-800',
            light: 'text-gray-900 bg-white border border-gray-300',
            green: 'text-white bg-green-700',
            red: 'text-white bg-red-700',
            purple: 'text-white bg-purple-700',
            ghost: '',
          }[color],
        })),

      // DISABLED OUTLINED BUTTONS
      ...colors.map((color) => ({
        outline: true,
        disabled: true,
        color: color as ColorType,
        class: {
          primary: 'text-blue-700 border border-blue-700',
          dark: 'text-gray-900 border border-gray-800',
          light: 'border text-theme-500 border-gray-300',
          green: 'text-green-700 border border-green-700',
          red: 'text-red-700 border border-red-700',
          purple: 'text-purple-700 border border-purple-700',
          ghost: 'text-gray-600 bg-gray-50 rounded-md font-light border border-theme-300',
        }[color],
      })),
    ],
  })({
    size: props.size,
    outline: props.outline,
    pill: props.pill,
    disabled: props.disabled,
    color: props.color as ColorType,
  });
});
</script>
